import { applyMoveToGameBoardState } from "./gameBoardSlice";
import { LONG_NARDY_HEAD, MOVE_FROM_INDICES } from "../Constants";
import { GameBoardState, GameMode, HitStatus, Move, Player, PointState } from "../Types";

// ─── Контекст длинных нард, передаётся в функции валидации ───────────────────
export interface LongNardyContext {
  provisionalMoves: Move[];  // ходы уже сделанные в этом ходу
}

/** Сколько шашек уже ушло с "головы" в этом ходу */
function countFromHead(provisionalMoves: Move[], player: Player): number {
  const head = LONG_NARDY_HEAD[player];
  return provisionalMoves.filter(m => m.checkerOwner === player && m.from === head).length;
}

/** Первый ход игрока = все шашки ещё стоят на голове (с учётом того, что
 *  в текущем ходу часть могла быть перемещена — считаем «виртуальный» исходный счёт).
 */
function isPlayerFirstTurn(
  gameBoardState: GameBoardState,
  player: Player,
  provisionalMoves?: Move[],
): boolean {
  const head = LONG_NARDY_HEAD[player];
  // Текущие шашки на голове + те, что уже ушли с головы в этом ходу
  const currentOnHead = gameBoardState.pointsState[head][player];
  const movedFromHead = provisionalMoves
    ? provisionalMoves.filter(m => m.checkerOwner === player && m.from === head).length
    : 0;
  return (currentOnHead + movedFromHead) === 15;
}

/**
 * Первый ход: можно ли снять вторую шашку с головы?
 * Правило: разрешено ТОЛЬКО при дублях 3-3, 4-4 или 6-6 на ПЕРВОМ ходу.
 * Оптимизатор (maxMoveCount) при этом выберет нужный вариант.
 */
function firstTurnAllowsTwoFromHead(availableDice?: number[]): boolean {
  if (!availableDice || availableDice.length !== 4) return false;
  const die = availableDice[0];
  // Проверяем что это дубль (все четыре одинаковые) и значение 3, 4 или 6
  if (!availableDice.every(d => d === die)) return false;
  return die === 3 || die === 4 || die === 6;
}

/** На первом ходе вторую шашку с головы можно брать только если
 *  этим же значением нельзя сходить никакой другой шашкой на поле.
 */
function hasLegalMoveOutsideHead(
  gameBoardState: GameBoardState,
  currentPlayer: Player,
  dieValue: number,
  longNardyCtx?: LongNardyContext,
  availableDice?: number[]
): boolean {
  const head = LONG_NARDY_HEAD[currentPlayer];
  for (let i = 0; i < 24; i++) {
    if (i === head) continue;
    if (gameBoardState.pointsState[i][currentPlayer] <= 0) continue;
    const move = getMoveIfValid(
      gameBoardState,
      i,
      dieValue,
      currentPlayer,
      GameMode.LongNardy,
      longNardyCtx,
      availableDice
    );
    if (move != null) return true;
  }
  return false;
}

export function getPointStateAtIndex(
  gameBoardState: GameBoardState,
  pointIndex: number | "BAR" | "HOME"
): PointState {
  if (pointIndex === "BAR") {
    return gameBoardState.barState;
  } else if (pointIndex === "HOME") {
    return gameBoardState.homeState;
  } else {
    return gameBoardState.pointsState[pointIndex];
  }
}

export function doesPlayerOccupyBar(
  gameBoardState: GameBoardState,
  player: Player
): boolean {
  const barState = getPointStateAtIndex(gameBoardState, "BAR");
  return barState[player] > 0;
}

export function getIndexAfterMoving(
  fromPoint: number | "BAR",
  dieValue: number,
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon
): number | "HOME" {
  if (gameMode === GameMode.LongNardy) {
    // Длинные нарды: оба игрока идут против часовой стрелки (direction +1, с оборотом через 24)
    // P1: 0→1→...→23→HOME (дом = пройти все 24 точки)
    // P2: 12→13→...→23→0→1→...→11→HOME
    if (fromPoint === "BAR") return "HOME"; // не бывает в длинных

    const head = LONG_NARDY_HEAD[currentPlayer];
    // Расстояние от головы до текущей позиции (по кругу)
    const posFromHead = (fromPoint as number - head + 24) % 24;
    const newPosFromHead = posFromHead + dieValue;

    if (newPosFromHead >= 24) {
      return "HOME";
    }
    return (head + newPosFromHead) % 24;
  }

  // Короткие нарды
  const direction = currentPlayer === Player.One ? -1 : 1;
  const origin =
    fromPoint === "BAR" ? (currentPlayer === Player.One ? 24 : -1) : fromPoint;
  const destination = origin + dieValue * direction;

  if (currentPlayer === Player.One) {
    return destination < 0 ? "HOME" : destination;
  } else {
    return destination > 23 ? "HOME" : destination;
  }
}

export enum CanOccupyResult {
  No = "NO",
  Yes = "YES",
  Hit = "HIT",
}

export function canPlayerOccupyPoint(
  gameBoardState: GameBoardState,
  toPoint: number | "HOME",
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon
): CanOccupyResult {
  if (toPoint === "HOME") {
    return CanOccupyResult.Yes;
  }
  const pointState = getPointStateAtIndex(gameBoardState, toPoint);
  const otherPlayer = currentPlayer === Player.One ? Player.Two : Player.One;

  if (gameMode === GameMode.LongNardy) {
    // В длинных нардах нельзя ходить на любую точку, где есть хотя бы одна шашка соперника
    if (pointState[otherPlayer] >= 1) {
      return CanOccupyResult.No;
    }
    return CanOccupyResult.Yes;
  }

  // Короткие нарды: 2+ шашки блокируют, 1 шашка = бить
  if (pointState[otherPlayer] >= 2) {
    return CanOccupyResult.No;
  } else if (pointState[otherPlayer] === 1) {
    return CanOccupyResult.Hit;
  } else {
    return CanOccupyResult.Yes;
  }
}

export function hasAllCheckersInHomeBoard(
  gameBoardState: GameBoardState,
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon
): boolean {
  if (getPointStateAtIndex(gameBoardState, "BAR")[currentPlayer] > 0) {
    return false;
  }

  if (gameMode === GameMode.LongNardy) {
    // Длинные нарды: дом = последние 6 точек перед завершением круга
    // P1: дом = точки 18-23 (posFromHead 18..23)
    // P2: дом = точки 6-11  (posFromHead 18..23 от головы 12)
    const head = LONG_NARDY_HEAD[currentPlayer];
    for (let i = 0; i < 24; i++) {
      if (gameBoardState.pointsState[i][currentPlayer] > 0) {
        const posFromHead = (i - head + 24) % 24;
        if (posFromHead < 18) return false; // не в доме
      }
    }
    return true;
  }

  // Короткие нарды
  if (currentPlayer === Player.One) {
    for (let i = 6; i <= 23; i++) {
      if (getPointStateAtIndex(gameBoardState, i)[currentPlayer] > 0) {
        return false;
      }
    }
  } else {
    for (let i = 0; i <= 17; i++) {
      if (getPointStateAtIndex(gameBoardState, i)[currentPlayer] > 0) {
        return false;
      }
    }
  }
  return true;
}

export function getDistanceFromHome(
  fromPoint: number | "BAR",
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon
): number {
  if (fromPoint === "BAR") {
    return 25;
  }

  if (gameMode === GameMode.LongNardy) {
    const head = LONG_NARDY_HEAD[currentPlayer];
    const posFromHead = ((fromPoint as number) - head + 24) % 24;
    return 24 - posFromHead; // расстояние до конца круга
  }

  return currentPlayer === Player.One ? fromPoint + 1 : 24 - fromPoint;
}

/**
 * Длинные нарды: проверяем, что ход не создаёт заграждение, запирающее ВСЕ шашки противника.
 * Заграждение из 6+ подряд шашек допустимо только если хотя бы одна шашка противника
 * может добраться до HOME, не пересекая заграждение.
 */
function wouldBlockAllOpponent(
  gameBoardState: GameBoardState,
  fromPoint: number,
  toPoint: number,
  currentPlayer: Player,
): boolean {
  const otherPlayer = currentPlayer === Player.One ? Player.Two : Player.One;
  const otherHead = LONG_NARDY_HEAD[otherPlayer];

  // Симулируем ход: убираем с from, ставим на to
  const simPoints = gameBoardState.pointsState.map((ps, i) => {
    const copy = { ...ps };
    if (i === fromPoint) copy[currentPlayer] = Math.max(0, copy[currentPlayer] - 1);
    if (i === toPoint) copy[currentPlayer] = copy[currentPlayer] + 1;
    return copy;
  });

  // Находим все точки, занятые текущим игроком
  const occupied = new Array(24).fill(false);
  for (let i = 0; i < 24; i++) {
    if (simPoints[i][currentPlayer] > 0) occupied[i] = true;
  }

  // Находим непрерывную цепочку, содержащую toPoint (по кругу 0..23)
  let chainStart = toPoint;
  let chainLen = 1;
  for (let k = 1; k < 24; k++) {
    const prev = (toPoint - k + 24) % 24;
    if (occupied[prev]) { chainStart = prev; chainLen++; }
    else break;
  }
  for (let k = 1; k < 24; k++) {
    const next = (toPoint + k) % 24;
    if (occupied[next]) chainLen++;
    else break;
  }

  // Если цепочка < 6 — запирания нет
  if (chainLen < 6) return false;

  // Собираем позиции цепочки в системе координат оппонента
  const chainPosForOpp = new Set<number>();
  for (let k = 0; k < chainLen; k++) {
    const pt = (chainStart + k) % 24;
    chainPosForOpp.add((pt - otherHead + 24) % 24);
  }

  // Проверяем каждую шашку оппонента: может ли хотя бы одна дойти до HOME
  // не пересекая ни одну точку заграждения (posFromHead от текущей+1 до 23)?
  if (gameBoardState.homeState[otherPlayer] > 0) return false; // уже есть в HOME

  for (let i = 0; i < 24; i++) {
    if (simPoints[i][otherPlayer] > 0) {
      const posForOpp = (i - otherHead + 24) % 24;
      let canReachHome = true;
      for (let p = posForOpp + 1; p <= 23; p++) {
        if (chainPosForOpp.has(p)) { canReachHome = false; break; }
      }
      if (canReachHome) return false; // эта шашка «впереди» — заграждение допустимо
    }
  }

  // Все шашки противника заблокированы заграждением — нельзя
  return true;
}

export function getMoveIfValid(
  gameBoardState: GameBoardState,
  fromPoint: number | "BAR",
  dieValue: number,
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext,
  availableDice?: number[]
): Move | null {
  const fromPointState = getPointStateAtIndex(gameBoardState, fromPoint);

  // Move is invalid if player does not actually occupy the fromPoint.
  if (fromPointState[currentPlayer] === 0) {
    return null;
  }

  if (gameMode === GameMode.LongNardy) {
    // В длинных нардах нет бара — нельзя ходить с него
    if (fromPoint === "BAR") {
      return null;
    }

    // ─── Правило головы ────────────────────────────────────────────────
    const head = LONG_NARDY_HEAD[currentPlayer];
    if (fromPoint === head && longNardyCtx) {
      const alreadyFromHead = countFromHead(longNardyCtx.provisionalMoves, currentPlayer);
      if (alreadyFromHead >= 1) {
        // Вторую шашку с головы можно брать только на первом ходу,
        // при спец-дублях 3-3/4-4/6-6 и только если нет легального
        // хода другой шашкой на поле.
        const isFirst = isPlayerFirstTurn(gameBoardState, currentPlayer, longNardyCtx.provisionalMoves);
        const noOtherFieldMove = !hasLegalMoveOutsideHead(
          gameBoardState,
          currentPlayer,
          dieValue,
          longNardyCtx,
          availableDice
        );
        if (
          !isFirst ||
          !firstTurnAllowsTwoFromHead(availableDice) ||
          !noOtherFieldMove
        ) {
          return null; // с головы уже ушла 1 шашка — нельзя
        }
        if (alreadyFromHead >= 2) {
          return null; // даже с исключением максимум 2
        }
      }
    }
  } else {
    // Короткие нарды: с бара нужно входить первым
    if (
      doesPlayerOccupyBar(gameBoardState, currentPlayer) &&
      fromPoint !== "BAR"
    ) {
      return null;
    }
  }

  const toPointIndex = getIndexAfterMoving(fromPoint, dieValue, currentPlayer, gameMode);

  // If the move would bear the checker off the board.
  if (toPointIndex === "HOME" && fromPoint !== "BAR") {
    // Move is invalid if not all checkers are in home board.
    if (!hasAllCheckersInHomeBoard(gameBoardState, currentPlayer, gameMode)) {
      return null;
    }

    // If the die roll is greater than the distance to home.
    if (dieValue > getDistanceFromHome(fromPoint, currentPlayer, gameMode)) {
      if (gameMode === GameMode.LongNardy) {
        // В длинных нардах: проверяем что нет шашек ещё дальше от дома
        const head = LONG_NARDY_HEAD[currentPlayer];
        const myPosFromHead = ((fromPoint as number) - head + 24) % 24;
        for (let i = 0; i < 24; i++) {
          if (gameBoardState.pointsState[i][currentPlayer] > 0) {
            const pos = (i - head + 24) % 24;
            if (pos < myPosFromHead) return null; // есть шашка дальше от дома
          }
        }
      } else {
        if (currentPlayer === Player.One) {
          for (let i = (fromPoint as number) + 1; i <= 5; i++) {
            if (getPointStateAtIndex(gameBoardState, i)[Player.One] > 0) {
              return null;
            }
          }
        } else {
          for (let i = (fromPoint as number) - 1; i >= 18; i--) {
            if (getPointStateAtIndex(gameBoardState, i)[Player.Two] > 0) {
              return null;
            }
          }
        }
      }
    }
  }

  const canOccupy = canPlayerOccupyPoint(gameBoardState, toPointIndex, currentPlayer, gameMode);
  if (canOccupy === CanOccupyResult.Yes || canOccupy === CanOccupyResult.Hit) {
    // ─── Длинные нарды: правило «нельзя запереть все 15 шашек противника» ──────
    // Заграждение (6 подряд) допустимо только если хотя бы одна шашка противника
    // находится «впереди» заграждения (уже прошла его).
    if (gameMode === GameMode.LongNardy && typeof toPointIndex === "number") {
      if (wouldBlockAllOpponent(gameBoardState, fromPoint as number, toPointIndex, currentPlayer)) {
        return null;
      }
    }

    const hitStatus =
      canOccupy === CanOccupyResult.Hit ? HitStatus.IsHit : HitStatus.NoHit;
    return {
      from: fromPoint,
      to: toPointIndex,
      dieUsed: dieValue,
      hitStatus,
      checkerOwner: currentPlayer,
    };
  }

  return null;
}

export function getInverseMove(move: Move): Move {
  return {
    from: move.to,
    to: move.from,
    dieUsed: move.dieUsed,
    hitStatus:
      move.hitStatus === HitStatus.IsHit
        ? HitStatus.UndoesHit
        : HitStatus.NoHit,
    checkerOwner: move.checkerOwner,
  };
}

/**
 * @param gameBoardState
 * @param dieRolls
 * @param currentPlayer
 * @returns List of all move sets (sequence of one or more moves) that the current player can
 *          make starting from the given point using one or more of the given dice.
 */
export function getAllMoveSetsFromStartingPoint(
  gameBoardState: GameBoardState,
  dieRolls: number[],
  fromPoint: number | "BAR",
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): Move[][] {
  let result: Move[][] = [];
  result = result.concat(
    getAllMoveSetsFromStartingPointImpl(
      gameBoardState,
      fromPoint,
      dieRolls,
      0,
      currentPlayer,
      [],
      gameMode,
      longNardyCtx
    )
  );

  if (dieRolls.length === 2 && dieRolls[0] !== dieRolls[1]) {
    const reversedDieRolls = [...dieRolls].reverse();
    result = result.concat(
      getAllMoveSetsFromStartingPointImpl(
        gameBoardState,
        fromPoint,
        reversedDieRolls,
        0,
        currentPlayer,
        [],
        gameMode,
        longNardyCtx
      )
    );
  }

  return result;
}

function getAllMoveSetsFromStartingPointImpl(
  gameBoardState: GameBoardState,
  fromPoint: number | "BAR" | "HOME",
  dieRolls: number[],
  dieIndex: number,
  currentPlayer: Player,
  resultSoFar: Move[][],
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): Move[][] {
  if (dieIndex >= dieRolls.length) {
    return resultSoFar;
  }

  const currentProvisionals = longNardyCtx
    ? { provisionalMoves: [...longNardyCtx.provisionalMoves, ...(resultSoFar.length > 0 ? resultSoFar[resultSoFar.length - 1] : [])] }
    : undefined;

  const move =
    fromPoint === "HOME"
      ? null
      : getMoveIfValid(gameBoardState, fromPoint, dieRolls[dieIndex], currentPlayer, gameMode, currentProvisionals, dieRolls);

  if (move == null) {
    return resultSoFar;
  } else {
    const newGameBoardState = applyMoveToGameBoardState(gameBoardState, move);
    let newMoveSet =
      resultSoFar.length > 0
        ? [[...resultSoFar[resultSoFar.length - 1], move]]
        : [[move]];

    return getAllMoveSetsFromStartingPointImpl(
      newGameBoardState,
      move.to,
      dieRolls,
      dieIndex + 1,
      currentPlayer,
      [...resultSoFar, ...newMoveSet],
      gameMode,
      longNardyCtx
    );
  }
}

/**
 * @param gameBoardState
 * @param dieRolls
 * @param currentPlayer
 * @returns List of all move sets (sequence of one or more moves) that the current player can
 *          make using the given dice. Each move set will contain the maximum number of legal moves
 *          possible for that scenario.
 */
export function getAllPossibleMoveSets(
  gameBoardState: GameBoardState,
  dieRolls: number[],
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): Move[][] {
  let result: Move[][] = [];
  result = result.concat(
    getAllPossibleMoveSetsImpl(gameBoardState, dieRolls, 0, currentPlayer, [], gameMode, longNardyCtx)
  );

  if (dieRolls.length === 2) {
    const reversedDieRolls = [...dieRolls].reverse();
    result = result.concat(
      getAllPossibleMoveSetsImpl(gameBoardState, reversedDieRolls, 0, currentPlayer, [], gameMode, longNardyCtx)
    );
  }

  return result;
}

export function getAllPossibleMoveSetsImpl(
  gameBoardState: GameBoardState,
  dieRolls: number[],
  dieIndex: number,
  currentPlayer: Player,
  moveSet: Array<Move>,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): Move[][] {
  if (dieIndex >= dieRolls.length) {
    return [[...moveSet]];
  }

  const currentCtx: LongNardyContext | undefined = longNardyCtx
    ? { provisionalMoves: [...longNardyCtx.provisionalMoves, ...moveSet] }
    : undefined;

  const allPossibleMoves = getAllPossibleMovesForGivenDieRoll(
    gameBoardState,
    dieRolls[dieIndex],
    currentPlayer,
    gameMode,
    currentCtx,
    dieRolls
  );

  if (allPossibleMoves.length === 0) {
    return [[...moveSet]];
  }

  if (dieIndex === dieRolls.length - 1) {
    return allPossibleMoves.map((move) => [...moveSet, move]);
  }

  let result: Move[][] = [];
  allPossibleMoves.forEach((move) => {
    const newGameBoardState = applyMoveToGameBoardState(gameBoardState, move);
    result = result.concat(
      getAllPossibleMoveSetsImpl(
        newGameBoardState,
        dieRolls,
        dieIndex + 1,
        currentPlayer,
        [...moveSet, move],
        gameMode,
        longNardyCtx
      )
    );
  });

  return result;
}

/**
 * @param gameBoardState
 * @param dice
 * @param currentPlayer
 * @returns List of all legal moves the given player can make using a single die from the provided
 *          dice values.
 */
export function getAllPossibleMovesForDice(
  gameBoardState: GameBoardState,
  dice: number[],
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): Move[] {
  let result: Move[] = [];
  let seenDieValues = new Set();
  for (let i = 0; i < dice.length; i++) {
    if (seenDieValues.has(dice[i])) {
      continue;
    }
    seenDieValues.add(dice[i]);
    result = result.concat(
      getAllPossibleMovesForGivenDieRoll(gameBoardState, dice[i], currentPlayer, gameMode, longNardyCtx, dice)
    );
  }
  return result;
}

/**
 * @param gameBoardState
 * @param dieRoll
 * @param currentPlayer
 * @returns List of all legal moves the given player can make using the single die value provided.
 */
export function getAllPossibleMovesForGivenDieRoll(
  gameBoardState: GameBoardState,
  dieRoll: number,
  currentPlayer: Player,
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext,
  allDice?: number[]
): Move[] {
  const moves: Move[] = [];
  MOVE_FROM_INDICES.forEach((from) => {
    const possibleMove = getMoveIfValid(
      gameBoardState,
      from,
      dieRoll,
      currentPlayer,
      gameMode,
      longNardyCtx,
      allDice
    );
    if (possibleMove !== null) {
      moves.push(possibleMove);
    }
  });

  return moves;
}

export function areProvisionalMovesSubmittable(
  gameBoardState: GameBoardState,
  dieRolls: number[],
  currentPlayer: Player,
  provisionalMoves: Move[],
  gameMode: GameMode = GameMode.Backgammon,
  longNardyCtx?: LongNardyContext
): boolean {
  const allPossibleMoveSets: Move[][] = getAllPossibleMoveSets(
    gameBoardState,
    dieRolls,
    currentPlayer,
    gameMode,
    longNardyCtx
  );

  const maxNumberOfMoves = allPossibleMoveSets.reduce(
    (maxNumberOfMoves, currMoveSet) =>
      currMoveSet.length > maxNumberOfMoves
        ? currMoveSet.length
        : maxNumberOfMoves,
    0
  );

  // The player did not use the max number of dice possible.
  if (provisionalMoves.length < maxNumberOfMoves) {
    return false;
  }

  const moveSetsWithMaxNumberOfMoves = allPossibleMoveSets.filter(
    (moveSet) => moveSet.length === maxNumberOfMoves
  );

  const maxPossibleDieValueUsed = moveSetsWithMaxNumberOfMoves.reduce(
    (prevMaxDieValueUsed, currMoveSet) => {
      const currMaxDieValueUsed: number = maxDieValueUsedInMoveSet(currMoveSet);
      return currMaxDieValueUsed > prevMaxDieValueUsed
        ? currMaxDieValueUsed
        : prevMaxDieValueUsed;
    },
    0
  );
  const maxProvisionalDieValueUsed = maxDieValueUsedInMoveSet(provisionalMoves);

  // The player must use the biggest die value possible.
  return maxProvisionalDieValueUsed === maxPossibleDieValueUsed;
}

export function maxDieValueUsedInMoveSet(moveSet: Move[]): number {
  return moveSet.reduce(
    (maxDieValueUsed, move) =>
      move.dieUsed > maxDieValueUsed ? move.dieUsed : maxDieValueUsed,
    0
  );
}
